import {BorcluTur} from "../uyap/CommonInterfaces";

/**
 * Faz 4 — volatil `dosyaId`'den KARARLI `dosya_ref`e köprü.
 *
 * PROBLEM: snapshot deposunun anahtarı `dosya_ref = "<birimId>:<dosyaEsasNo>"`
 * (Faz 1 ilkesi: şifreli ve sürekli değişen `dosyaId` anahtara ASLA girmez).
 * Okuma yönünde ise elimizde yalnız `dosyaId` var: sorgu istekleri UYAP'a onunla
 * gidiyor (`UyapRequest.talepData.dosyaId`).
 *
 * NEDEN PROP DEĞİL: bağlamı ekranlardan prop olarak geçirmek sihirbaz adım
 * dosyalarını değiştirmeyi gerektirirdi (bkz. icrapro-app/docs/sorgu-snapshot-sistemi.md
 * §2.4, İP3: sihirbaz dosyaları DEĞİŞMEZ). Üretimdeki ücretli sorguların hepsi zaten `DosyaBul().run(task)`
 * ile taze `dosyaId`'yi çözdükten SONRA koşuyor (TakipSorgula, TopluHacizHazirla,
 * TopluHacizGonder) — yani eşleme o anda elde. Burası o eşlemeyi bir kez alır ve
 * `run()` zincirine sunar.
 *
 * İKİNCİ KAYNAK: her sorgu cevabı `dosyaOzetDVO` (birimId + dosyaNo) taşıyor;
 * canlı cevap dönerken eşleme oradan da beslenir. Böylece bir dosyada ikinci
 * sorgu, birincinin cevabından öğrenilen eşlemeyle zincire girebilir.
 *
 * ÖMÜR: yalnız bellek. Kalıcılaştırılmadı, çünkü `dosyaId` oturumluk ve
 * kalıcılaştırılmış bayat bir eşleme (UYAP kimliği yeniden kullanırsa) YANLIŞ
 * dosyanın snapshot'ını okuturdu. Eşleme yoksa zincir sessizce bugünkü davranışa
 * düşer — hiçbir yerde hata üretmez.
 */

export interface DosyaKimligi {
    birimId: string;
    dosyaEsasNo: string;
    /**
     * İcrapro takip eşleşmesi biliniyorsa. Anahtar buna DAYANMAZ (Faz 1) ama
     * pakete taşınırsa sunucu `borclu_ref`i `tckn:`/`vkn:` olarak çözebilir —
     * ekran kaynaklı yazımın gece koşumuyla AYNI referansa düşmesi için gerekli.
     * Yoksa ekran satırı `kk:` altına düşer ve ancak sonraki gece koşumunda
     * `eskiFallbackiTasi` ile kalıcı referansa taşınır.
     */
    takipId: number | null;
}

/** Borçlu kimliğinin sunucu çözümlemesine yardım eden parçaları. */
export interface BorcluKimligi {
    /** Kurumda maskesiz gelir; kişide UYAP maskeli döndürdüğü için taşınmaz. */
    vergiNo?: string;
}

/**
 * Tavan: bot günlerce açık kalabiliyor ve gece koşumu binlerce dosya görüyor.
 * Tavana ulaşıldığında EN ESKİ giriş atılır (Map ekleme sırasını korur) — atılan
 * eşleme yalnız o dosyanın snapshot okumasını bugünkü davranışa düşürür, hiçbir
 * yerde hata üretmez.
 */
const AZAMI_KAYIT = 5000;

const dosyalar = new Map<string, DosyaKimligi>();
const borclular = new Map<number, BorcluKimligi>();

function tavanaSigdir(tablo: Map<any, any>): void {
    while (tablo.size > AZAMI_KAYIT) {
        const enEski = tablo.keys().next();
        if (enEski.done) {
            return;
        }
        tablo.delete(enEski.value);
    }
}

// kod inceleme: export edildi — TekilSnapshotYazici.ts aynı mantığı ikinci kez
// yazmak yerine buradan import ediyor (T2-34).
export function temizDize(deger: any): string {
    return deger === null || deger === undefined ? "" : String(deger).trim();
}

/**
 * B2 (bkz. icrapro-app/docs/sorgu-snapshot-sistemi.md §2.4): UYAP, birleşen dosyalarda esas no'yu
 * "2023/100840 - ( 2015/7071 )" gibi GÖSTERİM adıyla verebiliyor ve bu değer
 * görev kaydına sızabiliyor. Olduğu gibi anahtara girerse aynı dosya depoda iki
 * ayrı `dosya_ref` altında bölünür (görev kirli yazar, ekran temiz arar). Kural:
 * metindeki İLK `YYYY/N...` deseni esas no'dur; desen yoksa kırpılmış ham değere
 * düşülür. Sunucu (`SnapshotService::dosyaEsasNoNormalize`) aynı kuralı savunma
 * katmanı olarak uygular.
 */
export function dosyaEsasNoNormalize(ham: any): string {
    const deger = temizDize(ham);
    const eslesme = deger.match(/(\d{4}\/\d+)/);
    return eslesme ? eslesme[1] : deger;
}

/** `dosyaId -> {birimId, dosyaEsasNo, takipId}`. Eksik parça varsa kayıt YAPILMAZ. */
export function dosyayiKaydet(dosyaId: any, birimId: any, dosyaEsasNo: any, takipId: any = null): void {
    const anahtar = temizDize(dosyaId);
    const birim = temizDize(birimId);
    const esasNo = dosyaEsasNoNormalize(dosyaEsasNo);

    if (anahtar === "" || birim === "" || esasNo === "") {
        return;
    }

    // "0" ve boş değerler null'a indirgenir — SnapshotYazici ile aynı kural.
    const takip = temizDize(takipId);
    const cozulmusTakipId = takip === "" || takip === "0" ? null : Number(takip);

    const onceki = dosyalar.get(anahtar);
    dosyalar.set(anahtar, {
        birimId: birim,
        dosyaEsasNo: esasNo,
        // Cevaptan öğrenilen eşlemede takipId yoktur; daha önce DosyaBul'dan
        // öğrenilmiş bir takipId varsa KORUNUR (bilgi kaybı olmasın).
        takipId: cozulmusTakipId !== null && !isNaN(cozulmusTakipId)
            ? cozulmusTakipId
            : (onceki ? onceki.takipId : null),
    });
    tavanaSigdir(dosyalar);
}

/** Sorgu cevabındaki `dosyaOzetDVO`'dan eşlemeyi öğrenir. Şekil bozuksa sessiz çıkar. */
export function cevaptanOgren(cevap: any): void {
    const ozet = cevap?.dosyaOzetDVO;
    if (!ozet) {
        return;
    }

    dosyayiKaydet(ozet.dosyaId, ozet?.birimDVO?.birimId, ozet.dosyaNo);

    const borclu = cevap?.borcluBilgileri;
    if (borclu && borclu.turu === BorcluTur.KURUM) {
        // Kurum VKN'si UYAP'ta maskesiz gelir ve yazma yönünde `vkn:` referansının
        // 1. kaynağıdır (BorcluRefCozumleyici). Okuma yönü de aynı değeri sunucuya
        // verebilsin diye saklanır; kişi TCKN'si maskeli geldiği için saklanmaz.
        const vergiNo = temizDize(borclu?.kurumDVO?.vergiNo);
        if (borclu.kisiKurumId > 0 && vergiNo !== "") {
            borclular.set(Number(borclu.kisiKurumId), {vergiNo});
            tavanaSigdir(borclular);
        }
    }
}

/** Eşleme biliniyorsa dosya kimliği, bilinmiyorsa null. */
export function dosyaKimligiCoz(dosyaId: any): DosyaKimligi | null {
    return dosyalar.get(temizDize(dosyaId)) || null;
}

/** Eşleme biliniyorsa `"<birimId>:<dosyaEsasNo>"`, bilinmiyorsa null. */
export function dosyaRefCoz(dosyaId: any): string | null {
    const kimlik = dosyalar.get(temizDize(dosyaId));
    if (!kimlik) {
        return null;
    }

    // Sunucudaki türetimle (SnapshotService::dosyaRef) BİREBİR aynı: düz birleştirme,
    // önek yok. Kural burada ikinci kez yazılmadı, aynısı taklit edildi.
    return kimlik.birimId + ":" + kimlik.dosyaEsasNo;
}

/** Kurum borçlusunun bilinen maskesiz VKN'si (yoksa null). */
export function borclununVergiNosu(kisiKurumId: any): string | null {
    const kimlik = borclular.get(Number(kisiKurumId));

    return kimlik && kimlik.vergiNo ? kimlik.vergiNo : null;
}

/** Yalnız testler için: iki haritayı da boşaltır. */
export function hafizayiTemizle(): void {
    dosyalar.clear();
    borclular.clear();
}
